import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Container, Form, Modal, Table } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import Select from 'react-select';
import { isValid, toBBAN, fromBBAN } from 'iban-ts';
import {
  ActionButtons,
  addStatusToast,
  clearStatusToasts,
  InputCheck,
  InputNumber,
  InputText,
  Loader,
  SectionHeader,
  VerticalTabPane,
  VerticalTabs,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import {
  AccountDetailsFromDb,
  AccountUpdateData,
  AddressResponse,
  AddressUpdateData,
  BankSettingsResponse,
  BankSettingsUpdateData,
  BookingNoteSettingsResponse,
  BookingNoteSettingsUpdateData,
  ContactUpdateData,
  EmailResponse,
  EmailUpdateData,
  getAccountRolesFromDbAsync,
  getAcountDetailsFromDbAsync,
  getAllRolesAsync,
  getBankSettingsAsync,
  getBookingNoteSettingsAsync,
  getOfficerSettingsAsync,
  getOmaLiittymaSettingsAsync,
  getVatSettingsAsync,
  OfficerResponse,
  OmaLiittymaResponse,
  OmaLiittymaUpdateData,
  PhoneResponse,
  PhoneUpdateData,
  resetAccountDetailsAsync,
  selectAccountDetailsFromDb,
  selectAccountDetailsLoadingStatus,
  selectAllRoles,
  selectBankSettings,
  selectBookingNoteData,
  selectOfficerSettingsData,
  selectOmaLiittymaData,
  selectRolesLoadingStatus,
  selectUserRoles,
  selectVatData,
  updateAccountDataAsync,
  UpdateBankSettinsAsync,
  UpdateBookingNoteSettinsAsync,
  UpdateOmaLiittymaSettinsAsync,
  UpdateVatSettinsAsync,
  VatSettingsResponse,
  VatSettingsUpdateData,
} from './identityUsersSlice';
import {
  getCountriesAsync,
  selectCountryItems,
} from '../countries/countriesSlice';
import { getSeasonRatesAsync } from '../seasonRates/seasonRatesSlice';

function AddNewIdentityUser() {
  const navigate = useNavigate();
  const backLinkUrl = '/users/search';
  const params = useParams();
  const dispatch = useAppDispatch();
  const allRoles = useAppSelector(selectAllRoles);
  const userRoles = useAppSelector(selectUserRoles);
  const rolesStatus = useAppSelector(selectRolesLoadingStatus);
  const countries = useAppSelector(selectCountryItems);
  const accountDetails = useAppSelector(selectAccountDetailsFromDb);
  const omaLiittymaSettings = useAppSelector(selectOmaLiittymaData);
  const bankSettings = useAppSelector(selectBankSettings);
  const officerSettings = useAppSelector(selectOfficerSettingsData);
  const bookingNoteSettings = useAppSelector(selectBookingNoteData);
  const vatSettings = useAppSelector(selectVatData);
  const accountLoadingStatus = useAppSelector(
    selectAccountDetailsLoadingStatus,
  );
  const [isAddressModalVisible, setIsAddressModalVisible] =
    useState<boolean>(false);
  const [isPhoneModalVisible, setIsPhoneModalVisible] =
    useState<boolean>(false);
  const [isEMailModalVisible, setIsEMailModalVisible] =
    useState<boolean>(false);

  function getIsOwner() {
    const filteredRoles =
      allRoles != null && allRoles.rolesList != null
        ? allRoles.rolesList.filter(
            ar =>
              userRoles.includes(ar.id) &&
              (ar.name.includes('Owner') || ar.name.includes('Maintainer')),
          )
        : null;
    return filteredRoles != null && filteredRoles.length > 0;
  }

  function getIsOfficer() {
    const filteredRoles =
      allRoles != null && allRoles.rolesList != null
        ? allRoles.rolesList.filter(
            ar =>
              userRoles.includes(ar.id) && ar.name.includes('Administrator'),
          )
        : null;
    return filteredRoles != null && filteredRoles.length > 0;
  }

  const Tabs: string[] = [
    'Perustiedot',
    'Yhteystiedot',
    'Roolit',
    'Omaliittymä ja Rooli asetukset',
    'Varaustiedotteiden lähetys asetukset',
    'Pankki asetukset',
    'Virkailijan asetukset ja tiedot',
    'Alv asetukset',
  ];
  const [activeTab, setActiveTab] = useState<string>(Tabs[0]);
  const [editedOmaLiittymaSettings, setEditedOmaLiittymaSettings] =
    useState<OmaLiittymaResponse>({
      id: '',
      caretakerNumber: null,
      directLinkPath: '',
      hideBillOwnerData: false,
      ownerNumber: null,
      accountId: '',
      lastRequestDate: '',
    } as OmaLiittymaResponse);
  const [editedBookingNoteSettings, setEditedBookingNoteSettings] =
    useState<BookingNoteSettingsResponse>({
      id: '',
      accountId: '',
      bookingNoteByEmail: false,
      bookingNoteByMail: false,
      bookingNoteBySms: false,
      bookingNoteImmediatelly: false,
      bookingNoteWeekly: false,
      noMassSms: false,
    } as BookingNoteSettingsResponse);

  const [editedVatSettings, setEditedVatSettings] =
    useState<VatSettingsResponse>({
      id: '',
      accountId: '',
      hetu: '',
      otherVatCode: '',
      paysVat: false,
      yCode: '',
    } as VatSettingsResponse);

  const [editedBankSettings, setEditedBankSettings] =
    useState<BankSettingsResponse>({
      id: '',
      accountId: '',
      bic: '',
      iban: '',
    } as BankSettingsResponse);

  const [editedOfficerSettings, setEditedOfficerSettings] =
    useState<OfficerResponse>({
      id: '',
      officerNumber: '',
      accountId: '',
    } as OfficerResponse);

  const [editedEmail, setEditedEmail] = useState<EmailResponse>({
    id: '',
    description: '',
    emailAddress: '',
    isPrimary: false,
    isBookingNoteEmail: false,
  } as EmailResponse);

  const [editedPhone, setEditedPhone] = useState<PhoneResponse>({
    id: '',
    typeId: '',
    phoneNumber: '',
    description: '',
    isPrimary: false,
  } as PhoneResponse);

  const [editedAddress, setEditedAddress] = useState<AddressResponse>({
    id: null,
    postStreetAddress: '',
    postCity: '',
    postCode: '',
    isPrimary: false,
    description: '',
    additionalAddressLine: '',
    countryId: '',
  } as AddressResponse);

  const [editedRoles, setEditedRoles] = useState<string[]>([]);

  const [editedAccount, setEditedAccount] = useState<AccountDetailsFromDb>({
    id: 'new',
    addresses: [
      {
        id: '',
        postStreetAddress: '',
        postCity: '',
        postCode: '',
        isPrimary: false,
        description: '',
        additionalAddressLine: '',
        countryId: '',
      },
    ] as AddressResponse[],
    companyName: '',
    additionalInformation: '',
    description: '',
    emails: [
      {
        id: '',
        description: '',
        emailAddress: '',
        isPrimary: false,
        isBookingNoteEmail: false,
      } as EmailResponse,
    ],
    firstName: '',
    lastName: '',
    phones: [
      {
        id: '',
        typeId: '',
        phoneNumber: '',
        description: '',
        isPrimary: false,
      } as PhoneResponse,
    ],
    www: '',
    subjectId: '',
  } as AccountDetailsFromDb);

  function resetData() {
    setEditedOmaLiittymaSettings({
      id: '',
      caretakerNumber: null,
      directLinkPath: '',
      hideBillOwnerData: false,
      ownerNumber: null,
      accountId: '',
      lastRequestDate: '',
    } as OmaLiittymaResponse);
    setEditedOfficerSettings({
      id: '',
      officerNumber: '',
      accountId: '',
    } as OfficerResponse);
    dispatch(
      resetAccountDetailsAsync({
        id: 'new',
        addresses: [
          {
            id: '',
            postStreetAddress: '',
            postCity: '',
            postCode: '',
            isPrimary: false,
            description: '',
            additionalAddressLine: '',
            countryId: '',
          },
        ] as AddressResponse[],
        companyName: '',
        additionalInformation: '',
        description: '',
        emails: [
          {
            id: '',
            description: '',
            emailAddress: '',
            isPrimary: false,
            isBookingNoteEmail: false,
          } as EmailResponse,
        ],
        firstName: '',
        lastName: '',
        phones: [
          {
            id: '',
            typeId: '',
            phoneNumber: '',
            description: '',
            isPrimary: false,
          } as PhoneResponse,
        ],
        www: '',
        subjectId: '',
      } as AccountDetailsFromDb),
    );
    setEditedAccount({
      id: 'new',
      addresses: [
        {
          id: '',
          postStreetAddress: '',
          postCity: '',
          postCode: '',
          isPrimary: false,
          description: '',
          additionalAddressLine: '',
          countryId: '',
        },
      ] as AddressResponse[],
      companyName: '',
      additionalInformation: '',
      description: '',
      emails: [
        {
          id: '',
          description: '',
          emailAddress: '',
          isPrimary: false,
          isBookingNoteEmail: false,
        } as EmailResponse,
      ],
      firstName: '',
      lastName: '',
      phones: [
        {
          id: '',
          typeId: '',
          phoneNumber: '',
          description: '',
          isPrimary: false,
        } as PhoneResponse,
      ],
      www: '',
      subjectId: '',
    } as AccountDetailsFromDb);
    setEditedPhone({
      id: '',
      typeId: '',
      phoneNumber: '',
      description: '',
      isPrimary: false,
    } as PhoneResponse);
    setEditedAddress({
      id: null,
      postStreetAddress: '',
      postCity: '',
      postCode: '',
      isPrimary: false,
      description: '',
      additionalAddressLine: '',
      countryId: '',
    } as AddressResponse);
    setEditedBankSettings({
      id: '',
      accountId: '',
      bic: '',
      iban: '',
    } as BankSettingsResponse);
    setEditedBookingNoteSettings({
      id: '',
      accountId: '',
      bookingNoteByEmail: false,
      bookingNoteByMail: false,
      bookingNoteBySms: false,
      bookingNoteImmediatelly: false,
      bookingNoteWeekly: false,
      noMassSms: false,
    } as BookingNoteSettingsResponse);
    setEditedEmail({
      id: '',
      description: '',
      emailAddress: '',
      isPrimary: false,
      isBookingNoteEmail: false,
    } as EmailResponse);
    setEditedRoles([]);
    setEditedVatSettings({
      id: '',
      accountId: '',
      hetu: '',
      otherVatCode: '',
      paysVat: false,
      yCode: '',
    } as VatSettingsResponse);
  }
  const initFetch = useCallback(() => {
    resetData();
    dispatch(
      getCountriesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
        culture: LOCALES.Fi,
      }),
    );
    dispatch(
      getAllRolesAsync({
        pageNumber: 1,
        pageSize: 10,
        culture: 'fi_FI',
        searchString: '',
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch, params.identityId]);

  useEffect(() => {
    if (accountDetails != null) {
      setEditedAccount({
        id: '',
        addresses: [
          {
            id: '',
            postStreetAddress: '',
            postCity: '',
            postCode: '',
            isPrimary: false,
            description: '',
            additionalAddressLine: '',
            countryId: '',
          },
        ] as AddressResponse[],
        companyName: '',
        additionalInformation: '',
        description: '',
        emails: [
          {
            id: '',
            description: '',
            emailAddress: '',
            isPrimary: false,
            isBookingNoteEmail: false,
          } as EmailResponse,
        ],
        firstName: '',
        lastName: '',
        phones: [
          {
            id: '',
            typeId: '',
            phoneNumber: '',
            description: '',
            isPrimary: false,
          } as PhoneResponse,
        ],
        www: '',
        subjectId: '',
      });
    } else {
      setEditedAccount(accountDetails);
    }
  }, [accountDetails]);

  useEffect(() => {
    if (userRoles != null) {
      setEditedRoles(userRoles);
    }
    if (
      getIsOwner() &&
      accountLoadingStatus === LOADING_STATUS.IDLE &&
      accountDetails != null &&
      accountDetails.id !== 'new'
    ) {
      dispatch(getOmaLiittymaSettingsAsync(accountDetails.id));
      dispatch(getBookingNoteSettingsAsync(accountDetails.id));
      dispatch(getBankSettingsAsync(accountDetails.id));
      dispatch(getVatSettingsAsync(accountDetails.id));
    }
    if (
      getIsOfficer() &&
      accountLoadingStatus === LOADING_STATUS.IDLE &&
      accountDetails != null &&
      accountDetails.id !== 'new'
    ) {
      dispatch(getOfficerSettingsAsync(accountDetails.id));
    }
  }, [userRoles]);

  useEffect(() => {
    if (editedRoles != null) {
      setEditedRoles(editedRoles);
    } else {
      resetData();
    }
  }, [editedRoles, params.identityId]);

  useEffect(() => {
    if (
      omaLiittymaSettings != null &&
      omaLiittymaSettings.accountId.toLowerCase() ===
        editedAccount.id.toLowerCase()
    ) {
      setEditedOmaLiittymaSettings(omaLiittymaSettings);
    } else {
      resetData();
    }
  }, [omaLiittymaSettings]);
  useEffect(() => {}, [officerSettings]);
  useEffect(() => {
    if (
      bankSettings != null &&
      bankSettings.accountId.toLowerCase() === editedAccount.id.toLowerCase()
    ) {
      setEditedBankSettings(bankSettings);
    } else {
      resetData();
    }
  }, [bankSettings]);

  useEffect(() => {
    if (
      officerSettings != null &&
      officerSettings.accountId.toLowerCase() === editedAccount.id.toLowerCase()
    ) {
      setEditedOfficerSettings(officerSettings);
    } else {
      resetData();
    }
  }, [officerSettings]);

  useEffect(() => {
    if (
      bookingNoteSettings != null &&
      bookingNoteSettings.accountId.toLowerCase() ===
        editedAccount.id.toLowerCase()
    ) {
      setEditedBookingNoteSettings(bookingNoteSettings);
    } else {
      resetData();
    }
  }, [bookingNoteSettings]);
  useEffect(() => {
    if (
      vatSettings != null &&
      vatSettings.accountId.toLowerCase() === editedAccount.id.toLowerCase()
    ) {
      setEditedVatSettings(vatSettings);
    } else {
      resetData();
    }
  }, [vatSettings]);
  function isSelected(itemId) {
    if (editedRoles != null) {
      return editedRoles.some(s =>
        s.toUpperCase().includes(itemId.toUpperCase()),
      );
    }
    return false;
  }
  function getOmaLiittymaUpdateData() {
    return {
      id: editedOmaLiittymaSettings.id,
      accountId: editedAccount.id,
      caretakerNumber: editedOmaLiittymaSettings.caretakerNumber,
      directLinkPath: editedOmaLiittymaSettings.directLinkPath,
      hideBillOwnerData: editedOmaLiittymaSettings.hideBillOwnerData,
      ownerNumber: editedOmaLiittymaSettings.ownerNumber,
    } as OmaLiittymaUpdateData;
  }
  function getBookingNoteUpdateData() {
    return {
      id: editedBookingNoteSettings.id,
      bookingNoteByEmail: editedBookingNoteSettings.bookingNoteByEmail,
      bookingNoteByMail: editedBookingNoteSettings.bookingNoteByMail,
      bookingNoteBySms: editedBookingNoteSettings.bookingNoteBySms,
      bookingNoteImmediatelly:
        editedBookingNoteSettings.bookingNoteImmediatelly,
      bookingNoteWeekly: editedBookingNoteSettings.bookingNoteWeekly,
      accountId: editedAccount.id,
      noMassSms: editedBookingNoteSettings.noMassSms,
    } as BookingNoteSettingsUpdateData;
  }

  function getVatUpdateData() {
    const vatUpdateData = {
      id: editedVatSettings.id,
      hetu: editedVatSettings.hetu,
      accountId: editedAccount.id,
      otherVatCode: editedVatSettings.otherVatCode,
      paysVat: editedVatSettings.paysVat,
      yCode: editedVatSettings.yCode,
    } as VatSettingsUpdateData;

    return vatUpdateData;
  }
  function getBankSettingsUpdateData() {
    return {
      id: editedBankSettings.id,
      bic: editedBankSettings.bic,
      iban: editedBankSettings.iban,
      accountId: editedAccount.id,
    } as BankSettingsUpdateData;
  }
  function getUpdateData() {
    let accountUpdateData: AccountUpdateData;
    accountUpdateData = {
      id: 'new',
      contactUpdateData: null,
      accountsRoles: null,
    } as AccountUpdateData;
    if (
      editedAccount != null &&
      editedAccount.id !== 'new' &&
      editedAccount.id !== ''
    ) {
      accountUpdateData = { ...accountUpdateData, id: editedAccount.id };
    }
    if (
      editedAccount != null &&
      (editedAccount.firstName != null ||
        editedAccount.lastName != null ||
        editedAccount.www != null ||
        editedAccount.companyName != null ||
        editedAccount.description != null ||
        editedAccount.additionalInformation != null)
    ) {
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: {
          firstName: editedAccount.firstName,
          companyName: editedAccount.companyName,
          additionalInformation: editedAccount.additionalInformation,
          addresses: null,
          description: editedAccount.description,
          emails: null,
          lastName: editedAccount.lastName,
          www: editedAccount.www,
          phones: null,
        },
      };
    } else {
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: {
          firstName: null,
          companyName: null,
          additionalInformation: null,
          addresses: null,
          description: null,
          emails: null,
          lastName: null,
          www: null,
          phones: null,
        } as ContactUpdateData,
      };
    }

    if (editedAccount != null && editedAccount.emails != null) {
      let contactdata = { ...accountUpdateData.contactUpdateData };
      contactdata = {
        ...contactdata,
        emails: editedAccount.emails.map(data => {
          return {
            id: data.id === 'new' ? null : data.id,
            isPrimary: data.isPrimary,
            isBookingNoteEmail: data.isBookingNoteEmail,
            description: data.description,
            emailAddress: data.emailAddress,
          } as EmailUpdateData;
        }),
      };
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: contactdata,
      };
    }

    if (editedAccount != null && editedAccount.phones != null) {
      let contactdata = { ...accountUpdateData.contactUpdateData };
      contactdata = {
        ...contactdata,
        phones: editedAccount.phones.map(data => {
          return {
            id: data.id === 'new' ? null : data.id,
            isPrimary: data.isPrimary,
            description: data.description,
            phoneNumber: data.phoneNumber,
            typeId: data.typeId,
          } as PhoneUpdateData;
        }),
      };
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: contactdata,
      };
    }

    if (editedAccount != null && editedAccount.addresses != null) {
      let contactdata = { ...accountUpdateData.contactUpdateData };
      contactdata = {
        ...contactdata,
        addresses: editedAccount.addresses.map(data => {
          return {
            id: data.id === 'new' ? null : data.id,
            isPrimary: data.isPrimary,
            description: data.description,
            additionalAddressLine: data.additionalAddressLine,
            countryId: data.countryId === '' ? null : data.countryId,
            postCode: data.postCode,
            postCity: data.postCity,
            postStreetAddress: data.postStreetAddress,
          } as AddressUpdateData;
        }),
      };
      accountUpdateData = {
        ...accountUpdateData,
        contactUpdateData: contactdata,
      };
    }
    if (userRoles != null) {
      const temproles = [...editedRoles];
      const updatedRoles = temproles.map(tr => tr);
      accountUpdateData = { ...accountUpdateData, accountsRoles: updatedRoles };
    }
    return accountUpdateData;
  }
  function addRemoveSelectedRole(event, roleId) {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (value === true) {
      if (
        editedRoles.some(s =>
          s.toUpperCase().includes(roleId.toUpperCase()),
        ) === true
      ) {
        return;
      }

      const tempuserRoles = [...editedRoles, roleId.toUpperCase().toString()];
      setEditedRoles(tempuserRoles);
    } else if (
      editedRoles.some(s => s.toUpperCase().includes(roleId.toUpperCase()))
    ) {
      const itemIndex = editedRoles.findIndex(o =>
        o.toUpperCase().includes(roleId.toUpperCase()),
      );
      if (itemIndex > -1) {
        const tempUserRoles = [...editedRoles];
        tempUserRoles.splice(itemIndex, 1);
        setEditedRoles(tempUserRoles);
      }
    }
  }

  const rolesSelectOptions = allRoles?.rolesList.map(item => (
    <Form.Group controlId={item.id} className="mb-24" key={item.id}>
      <Form.Check
        className="mb-24"
        label={item.description ?? item.name}
        name={item.name}
        checked={isSelected(item.id.toUpperCase())}
        onChange={event => addRemoveSelectedRole(event, item.id)}
      />
    </Form.Group>
  ));
  const handleSelectChangeAddress = selectedOption => {
    if (
      editedAccount.addresses != null &&
      selectedOption != null &&
      selectedOption.id != null
    ) {
      const data = editedAccount.addresses.filter(
        add => add.id === selectedOption.id,
      );
      if (data.length > 0) {
        setEditedAddress(data[0]);
      }
    }
  };

  const handleSelectChangePhone = selectedOption => {
    if (
      editedAccount.phones != null &&
      selectedOption != null &&
      selectedOption.id != null
    ) {
      const data = editedAccount.phones.filter(
        add => add.id === selectedOption.id,
      );
      if (data.length > 0) {
        setEditedPhone(data[0]);
      }
    }
  };
  const handleSelectChangeEmail = selectedOption => {
    if (
      editedAccount.emails != null &&
      selectedOption != null &&
      selectedOption.id != null
    ) {
      const data = editedAccount.emails.filter(
        add => add.id === selectedOption.id,
      );
      if (data.length > 0) {
        setEditedEmail(data[0]);
      }
    }
  };
  const handleEditAddressClick = selectedOption => {
    handleSelectChangeAddress(selectedOption);
    setIsAddressModalVisible(true);
  };

  const handleEditPhoneClick = selectedOption => {
    handleSelectChangePhone(selectedOption);
    setIsPhoneModalVisible(true);
  };

  const handleEditEmailClick = selectedOption => {
    handleSelectChangeEmail(selectedOption);
    setIsEMailModalVisible(true);
  };

  function removeAddress(removedItem) {
    if (editedAccount != null && editedAccount.addresses != null) {
      const tempaddresses = editedAccount.addresses.filter(
        item => item.id !== removedItem.id,
      );

      const storedAccount = {
        ...editedAccount,
        addresses: tempaddresses,
      };
      setEditedAccount(storedAccount);
    }
  }

  function removePhone(removedItem) {
    if (editedAccount != null && editedAccount.phones != null) {
      const tempPhones = editedAccount.phones.filter(
        item => item.id !== removedItem.id,
      );

      const storedAccount = {
        ...editedAccount,
        phones: tempPhones,
      };
      setEditedAccount(storedAccount);
    }
  }

  function removeEmail(removedItem) {
    if (editedAccount != null && editedAccount.emails != null) {
      const tempEmail = editedAccount.emails.filter(
        item => item.id !== removedItem.id,
      );

      const storedAccount = {
        ...editedAccount,
        emails: tempEmail,
      };
      setEditedAccount(storedAccount);
    }
  }

  const accountAddressSelectOptions =
    accountLoadingStatus === LOADING_STATUS.IDLE &&
    editedAccount != null &&
    editedAccount.addresses != null ? (
      editedAccount?.addresses.map(item => (
        <tr key={item.id}>
          <td style={{ width: '100%' }}>{item.postStreetAddress ?? ''}</td>
          <td style={{ width: '100%' }}>{item.postCode ?? ''}</td>
          <td style={{ width: '100%' }}>{item.postCity ?? ''}</td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              size="sm"
              onClick={() => handleEditAddressClick(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td className="pe-0">
            <Button
              size="sm"
              variant="danger"
              onClick={() => removeAddress(item)}
            >
              X
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );

  const accountPhonesSelectOptions =
    accountLoadingStatus === LOADING_STATUS.IDLE &&
    editedAccount != null &&
    editedAccount.phones != null ? (
      editedAccount?.phones.map(item => (
        <tr key={item.id}>
          <td style={{ width: '100%' }}>{item.phoneNumber ?? ''}</td>
          <td style={{ width: '100%' }}>{item.typeId ?? ''}</td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              size="sm"
              onClick={() => handleEditPhoneClick(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td className="pe-0">
            <Button
              size="sm"
              variant="danger"
              onClick={() => removePhone(item)}
            >
              X
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );
  const handleInputChange = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedAccount, [name]: value };
    setEditedAccount(tempdata);
  };

  const handleInputChangeOmaLiittyma = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedOmaLiittymaSettings, [name]: value };
    setEditedOmaLiittymaSettings(tempdata);
  };

  const handleInputChangeBookingNote = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedBookingNoteSettings, [name]: value };
    setEditedBookingNoteSettings(tempdata);
  };

  const handleInputChangeVat = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedVatSettings, [name]: value };
    setEditedVatSettings(tempdata);
  };
  const handleInputChangeBankSettings = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedBankSettings, [name]: value };
    setEditedBankSettings(tempdata);
  };

  const handleInputChangeOfficerSettings = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...editedOfficerSettings, [name]: value };
    setEditedOfficerSettings(tempdata);
  };
  const updateAccount = event => {
    const updateData = getUpdateData();
    dispatch(updateAccountDataAsync(updateData))
      .then(res => {
        console.log('response', res);
        if (res.meta.requestStatus === 'fulfilled') {
          dispatch(clearStatusToasts());
          dispatch(
            addStatusToast({
              Message: `Käyttäjän perustiedot tallennettu.`,
            }),
          );
          navigate(`/users/details/${res.payload.result.toString()}`);
        }
      })
      .catch(() => {
        dispatch(clearStatusToasts());
        dispatch(
          addStatusToast({
            Message: `Käyttäjän perustiedot tallenuksessa virhe.`,
          }),
        );
      });
  };

  const updateOmaLiittmaSettings = event => {
    const updateData = getOmaLiittymaUpdateData();
    dispatch(UpdateOmaLiittymaSettinsAsync(updateData));
    dispatch(clearStatusToasts());
    dispatch(
      addStatusToast({
        Message: `Omaliittyma asetukset on tallennettu.`,
      }),
    );
  };

  const updateBookingNoteSettings = event => {
    const updateData = getBookingNoteUpdateData();
    if (
      updateData &&
      updateData !== null &&
      !updateData.bookingNoteByEmail &&
      !updateData.noBookingNote &&
      !updateData.bookingNoteBySms &&
      !updateData.bookingNoteByMail
    ) {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast({
          Message: `Email, Sms tai ei tiedotetta pitää olla valittuna.`,
          IsFailed: true,
        }),
      );
      return;
    }
    dispatch(UpdateBookingNoteSettinsAsync(updateData));
    dispatch(clearStatusToasts());
    dispatch(
      addStatusToast({
        Message: `Varaustiedote asetukset on tallennettu.`,
      }),
    );
  };

  const updateVatSettings = event => {
    const updateData = getVatUpdateData();
    dispatch(UpdateVatSettinsAsync(updateData));
    dispatch(clearStatusToasts());
    dispatch(
      addStatusToast({
        Message: `Alv asetukset on tallennettu.`,
      }),
    );
  };
  const updateBankSettingsData = event => {
    const updateData = getBankSettingsUpdateData();
    const ibaanValid = isValid(editedBankSettings.iban ?? '');
    if (!ibaanValid && editedBankSettings.iban !== '.') {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast({
          IsFailed: true,
          Message: `IBAN-tunnus on virheellinen.`,
        }),
      );
      return;
    }
    const bigValid = isValid(editedBankSettings.bic ?? '');
    if (bigValid && editedBankSettings.bic !== '') {
      dispatch(clearStatusToasts());
      dispatch(
        addStatusToast({
          IsFailed: true,
          Message: `BIC-TUNNUS on IBAN tunnus.`,
        }),
      );
      return;
    }
    dispatch(UpdateBankSettinsAsync(updateData)).then(res => {
      dispatch(clearStatusToasts());
      if (res.payload.ErrorCode != null && res.payload.ErrorCode !== '200') {
        dispatch(
          addStatusToast({
            Message: `Pankkiasetuksen tallennuksessta tapahtui virhe`,
          }),
        );
      } else {
        dispatch(
          addStatusToast({
            Message: `Pankkiasetukset on tallennettu.`,
          }),
        );
      }
    });
  };
  const editAddresses = (
    addressesIn: AddressResponse[],
    valuedata: AddressResponse,
  ) => {
    if (valuedata.id !== 'new') {
      return addressesIn.map(item => {
        if (item.id === valuedata.id) {
          item = valuedata;
          if (valuedata.isPrimary) {
            return { ...item, isPrimary: true };
          }
        } else if (valuedata.isPrimary) {
          item.isPrimary = false;
        }
        return item;
      });
    }
    valuedata.id = crypto.randomUUID();
    setEditedAddress(valuedata);
    addressesIn = [...addressesIn, valuedata];
    return addressesIn;
  };

  const editEmails = (
    addressesIn: EmailResponse[],
    valuedata: EmailResponse,
  ) => {
    if (valuedata.id !== 'new') {
      return addressesIn.map(item => {
        if (item.id === valuedata.id) {
          item = valuedata;
          if (valuedata.isPrimary) {
            return { ...item, isPrimary: true };
          }
        } else if (valuedata.isPrimary) {
          item.isPrimary = false;
        }
        return item;
      });
    }
    valuedata.id = crypto.randomUUID();
    setEditedEmail(valuedata);
    addressesIn = [...addressesIn, valuedata];
    return addressesIn;
  };

  const editPhones = (
    addressesIn: PhoneResponse[],
    valuedata: PhoneResponse,
  ) => {
    if (valuedata.id !== 'new') {
      return addressesIn.map(item => {
        if (item.id === valuedata.id) {
          item = valuedata;
          if (valuedata.isPrimary) {
            return { ...item, isPrimary: true };
          }
        } else if (valuedata.isPrimary) {
          item.isPrimary = false;
        }
        return item;
      });
    }
    valuedata.id = crypto.randomUUID();
    setEditedPhone(valuedata);
    addressesIn = [...addressesIn, valuedata];
    return addressesIn;
  };

  const addAddress = () => {
    const newaddress = {
      isPrimary: false,
      postStreetAddress: '',
      additionalAddressLine: '',
      postCity: '',
      countryId: null,
      postCode: '',
      description: '',
      id: 'new',
    } as AddressResponse;
    setEditedAddress(newaddress);
    let { addresses } = editedAccount;
    addresses = editAddresses(addresses, newaddress);
    const storeAccount = { ...editedAccount, addresses };
    setEditedAccount(storeAccount);
    setIsAddressModalVisible(true);
  };

  const addEmail = () => {
    const newemail = {
      isPrimary: false,
      isBookingNoteEmail: false,
      emailAddress: '',
      description: '',
      id: 'new',
    } as EmailResponse;
    setEditedEmail(newemail);
    let { emails } = editedAccount;
    emails = editEmails(emails, newemail);
    const customerDetailsStore = { ...editedAccount, emails };
    setEditedAccount(customerDetailsStore);
    setIsEMailModalVisible(true);
  };

  const addPhoneNumber = () => {
    const newPhone = {
      isPrimary: false,
      typeId: null,
      phoneNumber: '',
      description: '',
      id: 'new',
    } as PhoneResponse;
    setEditedPhone(newPhone);
    let { phones } = editedAccount;
    phones = editPhones(phones, newPhone);
    const customerDetailsStore = { ...editedAccount, phones };
    setEditedAccount(customerDetailsStore);
    setIsPhoneModalVisible(true);
  };
  const handleInputChangeEmail = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setEditedEmail({
      ...editedEmail,
      [name]: value,
    });
    const editedEmailTemp = {
      ...editedEmail,
      [name]: value,
    };
    let { emails } = editedAccount;
    emails = editEmails(emails, editedEmailTemp);
    const accountDetailsStore = { ...editedAccount, emails };
    setEditedAccount(accountDetailsStore);
  };

  const handleInputChangePhone = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setEditedPhone({
      ...editedPhone,
      [name]: value,
    });
    const editedPhoneTemp = {
      ...editedPhone,
      [name]: value,
    };
    let { phones } = editedAccount;
    phones = editPhones(phones, editedPhoneTemp);
    const accountDetailsStore = { ...editedAccount, phones };
    setEditedAccount(accountDetailsStore);
  };

  const countryOptions = countries.map(country => {
    return { value: country.id, label: country.name };
  });
  const handleInputChangeAddress = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    setEditedAddress({
      ...editedAddress,
      [name]: value,
    });
    const editedAddressTemp = {
      ...editedAddress,
      [name]: value,
    };
    let { addresses } = editedAccount;
    addresses = editAddresses(addresses, editedAddressTemp);
    const accountDetailsStore = { ...editedAccount, addresses };
    setEditedAccount(accountDetailsStore);
  };

  const handleSelectChange = (selectedOption, name) => {
    const newaddressData = { ...editedAddress };
    newaddressData[name] = selectedOption.value;
    setEditedAddress(newaddressData);
    let { addresses } = editedAccount;
    addresses = editAddresses(addresses, newaddressData);
    const accountDetailsStore = { ...editedAccount, addresses };
    setEditedAccount(accountDetailsStore);
  };

  const accountEmailsSelectOptions =
    accountLoadingStatus === LOADING_STATUS.IDLE &&
    editedAccount != null &&
    editedAccount.emails != null ? (
      editedAccount?.emails.map(item => (
        <tr key={item.id}>
          <td style={{ width: '100%' }}>{item.emailAddress ?? ''}</td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              size="sm"
              onClick={() => handleEditEmailClick(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td className="pe-0">
            <Button
              size="sm"
              variant="danger"
              onClick={() => removeEmail(item)}
            >
              X
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );

  return (
    <>
      <Helmet>
        <title>Käyttäjätiedot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Käyttäjät']}
          Title="Käyttäjän tiedot"
        />

        {accountLoadingStatus === LOADING_STATUS.LOADING && (
          <Loader IsLoading />
        )}

        {accountLoadingStatus === LOADING_STATUS.IDLE && (
          <VerticalTabs
            ActiveTab={activeTab}
            ChangeTab={setActiveTab}
            TabContainerId="customerTabs"
            Tabs={Tabs}
          >
            <VerticalTabPane IsActive={activeTab === Tabs[0]} Name={Tabs[0]}>
              {editedAccount != null && (
                <div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChange}
                      Id="firstName"
                      Name="firstName"
                      Value={editedAccount.firstName ?? ''}
                      IsRequired={false}
                      Label="Etunimi"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChange}
                      Id="lastName"
                      Name="lastName"
                      Value={editedAccount.lastName ?? ''}
                      IsRequired={false}
                      Label="Sukunimi / Yritys"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChange}
                      Id="www"
                      Name="www"
                      Value={editedAccount.www ?? ''}
                      IsRequired={false}
                      Label="Www"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChange}
                      Id="companyName"
                      Name="companyName"
                      Value={editedAccount.companyName ?? ''}
                      IsRequired={false}
                      Label="Yhteyshenkilö"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChange}
                      Id="description"
                      Name="description"
                      Value={editedAccount.description ?? ''}
                      IsRequired={false}
                      Label="Kuvaus"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChange}
                      Id="additionalInformation"
                      Name="additionalInformation"
                      Value={editedAccount.additionalInformation ?? ''}
                      IsRequired={false}
                      Label="Lisätiedot"
                    />
                  </div>
                </div>
              )}

              <ActionButtons
                CancelButtonUrl={backLinkUrl}
                HandleSave={event => updateAccount(event)}
              />
            </VerticalTabPane>

            <VerticalTabPane IsActive={activeTab === Tabs[1]} Name={Tabs[1]}>
              <div className="d-flex align-items-start mt-48">
                <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">Osoitteet</h2>
                <Button size="sm" onClick={addAddress}>
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää uusi
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <tbody>{accountAddressSelectOptions}</tbody>
                </Table>
              </div>
              <div className="d-flex align-items-start mt-48">
                <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">
                  Puhelinnumerot
                </h2>
                <Button size="sm" onClick={addPhoneNumber}>
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää uusi
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <tbody>{accountPhonesSelectOptions}</tbody>
                </Table>
              </div>
              <div className="d-flex align-items-start mt-48">
                <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">
                  Sähköposti-osoitteet
                </h2>
                <Button size="sm" onClick={addEmail}>
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää uusi
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <tbody>{accountEmailsSelectOptions}</tbody>
                </Table>
              </div>
              <ActionButtons
                CancelButtonUrl={backLinkUrl}
                HandleSave={event => updateAccount(event)}
              />
            </VerticalTabPane>

            <VerticalTabPane IsActive={activeTab === Tabs[2]} Name={Tabs[2]}>
              {rolesStatus === LOADING_STATUS.IDLE && (
                <div>{rolesSelectOptions}</div>
              )}
              <ActionButtons
                CancelButtonUrl={backLinkUrl}
                HandleSave={event => updateAccount(event)}
              />
            </VerticalTabPane>
            {rolesStatus === LOADING_STATUS.IDLE && getIsOwner() && (
              <VerticalTabPane IsActive={activeTab === Tabs[3]} Name={Tabs[3]}>
                <div>
                  <div className="mb-24">
                    <InputNumber
                      HandleChange={handleInputChangeOmaLiittyma}
                      Id="ownerNumber"
                      Name="ownerNumber"
                      Value={editedOmaLiittymaSettings.ownerNumber ?? ''}
                      IsRequired={false}
                      Label="Omistajanumero"
                      IsReadOnly
                    />
                  </div>
                  <div className="mb-24">
                    <InputNumber
                      HandleChange={handleInputChangeOmaLiittyma}
                      Id="caretakerNumber"
                      Name="caretakerNumber"
                      Value={editedOmaLiittymaSettings.caretakerNumber ?? ''}
                      IsRequired={false}
                      Label="HuoltajaNumero"
                      IsReadOnly
                    />
                  </div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeOmaLiittyma}
                      Id="hideBillOwnerData"
                      Name="hideBillOwnerData"
                      Checked={editedOmaLiittymaSettings.hideBillOwnerData}
                      IsRequired={false}
                      Label="Piilota laskulta"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeOmaLiittyma}
                      Id="directLinkPath"
                      Name="directLinkPath"
                      Value={editedOmaLiittymaSettings.directLinkPath ?? ''}
                      IsRequired={false}
                      Label="Suoralinkki"
                    />
                  </div>
                </div>
                <ActionButtons
                  CancelButtonUrl={backLinkUrl}
                  HandleSave={event => updateOmaLiittmaSettings(event)}
                />
              </VerticalTabPane>
            )}
            {rolesStatus === LOADING_STATUS.IDLE && getIsOwner() && (
              <VerticalTabPane IsActive={activeTab === Tabs[4]} Name={Tabs[4]}>
                <div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeBookingNote}
                      Id="bookingNoteWeekly"
                      Name="bookingNoteWeekly"
                      Checked={editedBookingNoteSettings.bookingNoteWeekly}
                      IsRequired={false}
                      Label="Varaustiedotteet viikoittain postin kautta"
                    />
                  </div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeBookingNote}
                      Id="bookingNoteImmediatelly"
                      Name="bookingNoteImmediatelly"
                      Checked={
                        editedBookingNoteSettings.bookingNoteImmediatelly
                      }
                      IsRequired={false}
                      Label="Varaustiedote heti"
                    />
                  </div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeBookingNote}
                      Id="bookingNoteBySms"
                      Name="bookingNoteBySms"
                      Checked={editedBookingNoteSettings.bookingNoteBySms}
                      IsRequired={false}
                      Label="Varaustiedote Sms viestinä"
                    />
                  </div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeBookingNote}
                      Id="bookingNoteByMail"
                      Name="bookingNoteByMail"
                      Checked={editedBookingNoteSettings.bookingNoteByMail}
                      IsRequired={false}
                      Label="Varaustiedote postin kautta"
                    />
                  </div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeBookingNote}
                      Id="bookingNoteByEmail"
                      Name="bookingNoteByEmail"
                      Checked={editedBookingNoteSettings.bookingNoteByEmail}
                      IsRequired={false}
                      Label="Varaustiedote heti sähköpostilla"
                    />
                  </div>
                </div>
                <ActionButtons
                  CancelButtonUrl={backLinkUrl}
                  HandleSave={event => updateBookingNoteSettings(event)}
                />
              </VerticalTabPane>
            )}
            {rolesStatus === LOADING_STATUS.IDLE && getIsOwner() && (
              <VerticalTabPane IsActive={activeTab === Tabs[5]} Name={Tabs[5]}>
                <div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeBankSettings}
                      Id="bic"
                      Name="bic"
                      Value={editedBankSettings.bic}
                      IsRequired={false}
                      Label="BIC"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeBankSettings}
                      Id="iban"
                      Name="iban"
                      Value={editedBankSettings.iban}
                      IsRequired={false}
                      Label="IBAN"
                    />
                  </div>
                </div>
                <ActionButtons
                  CancelButtonUrl={backLinkUrl}
                  HandleSave={event => updateBankSettingsData(event)}
                />
              </VerticalTabPane>
            )}
            {getIsOfficer() && editedOfficerSettings != null && (
              <VerticalTabPane IsActive={activeTab === Tabs[6]} Name={Tabs[6]}>
                <div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeOfficerSettings}
                      Id="officerNumber"
                      Name="officerNumber"
                      Value={
                        editedOfficerSettings.officerNumber != null
                          ? editedOfficerSettings.officerNumber
                          : ''
                      }
                      IsRequired={false}
                      IsReadOnly
                      Label="Virkailija numero"
                    />
                  </div>
                </div>
              </VerticalTabPane>
            )}
            {getIsOwner() && editedVatSettings != null && (
              <VerticalTabPane IsActive={activeTab === Tabs[7]} Name={Tabs[7]}>
                <div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeVat}
                      Id="yCode"
                      Name="yCode"
                      Value={
                        editedVatSettings.yCode != null
                          ? editedVatSettings.yCode
                          : ''
                      }
                      IsRequired={false}
                      Label="Y-tunnus"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeVat}
                      Id="hetu"
                      Name="hetu"
                      Value={
                        editedVatSettings.hetu != null
                          ? editedVatSettings.hetu
                          : ''
                      }
                      IsRequired={false}
                      Label="Hetu"
                    />
                  </div>
                  <div className="mb-24">
                    <InputText
                      HandleChange={handleInputChangeVat}
                      Id="otherVatCode"
                      Name="otherVatCode"
                      Value={
                        editedVatSettings.otherVatCode != null
                          ? editedVatSettings.otherVatCode
                          : ''
                      }
                      IsRequired={false}
                      Label="Muu Alv tunnus"
                    />
                  </div>
                  <div className="mb-24">
                    <InputCheck
                      HandleChange={handleInputChangeVat}
                      Id="paysVat"
                      Name="paysVat"
                      Checked={editedVatSettings.paysVat}
                      IsRequired={false}
                      Label="Alv velvollinen"
                    />
                  </div>
                  <ActionButtons
                    CancelButtonUrl={backLinkUrl}
                    HandleSave={event => updateVatSettings(event)}
                  />
                </div>
              </VerticalTabPane>
            )}
          </VerticalTabs>
        )}
        <Modal show={isAddressModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Muokkaa osoitetta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="postStreetAddress" className="mb-24">
              <Form.Label>Katuosoite</Form.Label>
              <Form.Control
                name="postStreetAddress"
                value={editedAddress.postStreetAddress ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="additionalAddressLine" className="mb-24">
              <Form.Label>Osoitteen lisärivi</Form.Label>
              <Form.Control
                name="additionalAddressLine"
                value={editedAddress.additionalAddressLine ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="postCode" className="mb-24">
              <Form.Label>Postinumero</Form.Label>
              <Form.Control
                name="postCode"
                value={editedAddress.postCode ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="postCity" className="mb-24">
              <Form.Label>Postitoimipaikka</Form.Label>
              <Form.Control
                name="postCity"
                value={editedAddress.postCity ?? ''}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group className="mb-24">
              <Form.Label>Maa</Form.Label>
              <Select
                inputId="countryId"
                name="countryId"
                placeholder="Valitse maa"
                isClearable
                isSearchable
                options={countryOptions}
                onChange={option => handleSelectChange(option, 'countryId')}
                value={countryOptions.filter(
                  option => option.value === editedAddress.countryId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <Form.Check
              className="my-24"
              label="Oletusosoite ?"
              id="isPrimary"
              name="isPrimary"
              onChange={handleInputChangeAddress}
              inline
              checked={editedAddress.isPrimary}
            />
          </Modal.Body>
          <Modal.Footer>
            <span className="text-danger">Muista tallentaa muutokset !</span>
            <Button
              variant="link"
              onClick={() => setIsAddressModalVisible(false)}
            >
              Sulje
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={isPhoneModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Muokkaa puhelinnumeroa</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="phoneNumber" className="mb-24">
              <Form.Label>Puhelinnumero</Form.Label>
              <Form.Control
                name="phoneNumber"
                value={editedPhone.phoneNumber ?? ''}
                onChange={handleInputChangePhone}
              />
            </Form.Group>

            <Form.Group controlId="description" className="mb-24">
              <Form.Label>Kuvaus</Form.Label>
              <Form.Control
                name="description"
                value={editedPhone.description ?? ''}
                onChange={handleInputChangePhone}
              />
            </Form.Group>
            <Form.Check
              className="my-24"
              label="Oletusnumero ?"
              id="isPrimary"
              name="isPrimary"
              onChange={handleInputChangePhone}
              inline
              checked={editedPhone.isPrimary}
            />
          </Modal.Body>
          <Modal.Footer>
            <span className="text-danger">Muista tallentaa muutokset !</span>
            <Button
              variant="link"
              onClick={() => setIsPhoneModalVisible(false)}
            >
              Sulje
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={isEMailModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Muokkaa sähköpostiosoitetta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="emailAddress" className="mb-24">
              <Form.Label>Sähköposti</Form.Label>
              <Form.Control
                name="emailAddress"
                value={editedEmail.emailAddress ?? ''}
                onChange={handleInputChangeEmail}
              />
            </Form.Group>

            <Form.Group controlId="description" className="mb-24">
              <Form.Label>Kuvaus</Form.Label>
              <Form.Control
                name="description"
                value={editedEmail.description ?? ''}
                onChange={handleInputChangeEmail}
              />
            </Form.Group>

            <Form.Check
              className="my-24"
              label="Oletus sähköposti ?"
              id="isPrimary"
              name="isPrimary"
              onChange={handleInputChangeEmail}
              inline
              checked={editedEmail.isPrimary}
            />
            <Form.Check
              className="my-24"
              label="Varaustiedote sähköposti ?"
              id="isBookingNoteEmail"
              name="isBookingNoteEmail"
              onChange={handleInputChangeEmail}
              inline
              checked={editedEmail.isBookingNoteEmail}
            />
          </Modal.Body>
          <Modal.Footer>
            <span className="text-danger">Muista tallentaa muutokset !</span>
            <Button
              variant="link"
              onClick={() => setIsEMailModalVisible(false)}
            >
              Sulje
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

export default AddNewIdentityUser;
